import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../auth/AuthProvider';
import { submitWaitlistEmail } from '../../data/cloud';
import { usePrerenderSignal } from '../../utils/usePrerenderSignal';
import { trackEvent } from '../../utils/analytics';
import {
  APP_STORE_URL,
  APP_STORE_FALLBACK,
  appStoreLive,
  captureTrafficSource,
  getTrafficSource,
  isAutomatedBrowser,
  isProductSurfaceAllowed,
} from './funnel/marketingConfig';
import IOSDevice from './IOSDevice';
import HeroTour from './HeroTour';
import './LandingPage.css';

// Analytics page id for the homepage. Funnel pages use their slug; the
// homepage reuses the same marketing_page_view / waitlist_signup event names
// (audit finding #6) with this page id so cross-page funnels join cleanly.
const HOME_PAGE = 'home';

// ----- Source-type trust strip (rendered as social proof) -----
// App Review 1.4.1 (2026-07-29): physician-attributed content was removed from
// the app, so these pills name the source types the app actually cites now
// (FDA labelling, peer-reviewed research) rather than claiming physician
// sourcing the product no longer has.
// Physician trust strip - intentionally NO names. Naming individual
// physicians without explicit endorsement opens us up to defamation /
// false-endorsement liability (per Lawrence 2026-05-10). The pill row
// below conveys credibility generically; specific source attribution
// lives inline on each protocol / KB entry where it's editorially in
// context (e.g. "per Seeds, Peptide Protocols Vol. 1").
const TRUST_PILLS = [
  'FDA Labeling',
  'Peer-Reviewed Research',
  'Every Claim Cited',
] as const;

// ----- App Store CTA action -----
// The Day-0 App Store flip lives in ONE place: funnel/marketingConfig.ts.
// This page imports APP_STORE_URL / APP_STORE_FALLBACK / appStoreLive from
// there so the badge routing here flips with every funnel page off the same
// constant (no local fallback to keep in sync).

// ----- Hero phone screens -----
const HERO_SCREENS = [
  { label: 'Schedule', src: '/images/landing/screen-schedule.png' },
  { label: 'Calculate', src: '/images/landing/calculator_hero.png' },
  { label: 'Mix', src: '/images/landing/reconstitution_hero.png' },
  { label: 'Library', src: '/images/landing/library_hero.png' },
] as const;

// ----- Walkthrough steps -----
// Copy voice: 5th-grade, short sentences, the ICP's own words (the shot,
// mixing, bac water, units) per .agents/product-marketing-context.md §9-10.
// Every bullet is a claim the shipped V1 app actually backs. Removed as
// unverified against the codebase (2026-07-22): saved-dose presets,
// unsafe-combination checks, timezone-aware reminders, side-effect logging.
const WALKTHROUGH_STEPS = [
  {
    title: 'From sealed vial of powder to your first shot in 10 minutes.',
    body: 'How much bac water goes in? How do you draw the right amount? What should it look like? PRTCL answers every question before you can worry about it.',
    bullets: [
      'Pictures at every step, so you know what it should look like',
      'The app does the math from your vial and your dose',
      'Every claim links to an FDA label or a published study',
      'Checkpoints so you never skip a step',
    ],
    src: '/images/landing/screen-mix.png',
  },
  {
    title: 'No mental math. No conversion charts.',
    body: 'Tell PRTCL your vial size and your dose. It shows you exactly how many units to draw, right on a picture of the syringe.',
    bullets: [
      'Works with any vial size and any peptide',
      'See the fill line on the syringe before you draw',
      'Free on the web, no account needed',
      'Shows units on a standard U-100 insulin syringe',
    ],
    src: '/images/landing/screen-calculate.png',
  },
  {
    title: 'Stay on schedule. Rotate your spots.',
    body: 'PRTCL picks a fresh spot for each shot, walks you through it, and logs it. You never hit the same spot twice in a row.',
    bullets: [
      'Rotates between belly, thigh, and arm spots',
      'Reminders so you never miss a shot',
      'Every shot saved in one simple timeline',
      'Send your log to your doctor in one tap',
    ],
    // Filenames in the handoff are cross-mapped: screen-learn.png is the
    // schedule UI; screen-schedule.png is the library/stack view.
    src: '/images/landing/screen-learn.png',
  },
] as const;

// ----- Comparison rows (rewritten 2026-05-09 from generic Reddit/YouTube/"Other apps"
// strawmen to real named competitors per `competitor-profiles/_landscape.md`).
// Cell states reflect honest landscape findings; PRTCL doesn't claim parity where
// real-only-reading would call it out as defensive.
// Refined 2026-05-09 (founder pass): removed "3-tier content" (covered elsewhere),
// shortened reconstitution row, swapped site-map for the more wedge-aligned
// "advanced protocols with dose ramps" (5th-grade for "titration"). -----
type CellState = 'yes' | 'partial' | 'no';
const COMPARE_ROWS: { label: string; prtcl: CellState; doseiq: CellState; shotsy: CellState; peptiq: CellState }[] = [
  { label: 'Walks first-timers from sealed vial to first injection', prtcl: 'yes',     doseiq: 'no',      shotsy: 'no',      peptiq: 'no'      },
  { label: 'Picture-led step-by-step during the actual injection',   prtcl: 'yes',     doseiq: 'no',      shotsy: 'partial', peptiq: 'no'      },
  { label: 'Every health claim links to a published source',        prtcl: 'yes',     doseiq: 'no',      shotsy: 'no',      peptiq: 'partial' },
  { label: 'Made for first-timers AND experienced users',            prtcl: 'yes',     doseiq: 'partial', shotsy: 'no',      peptiq: 'partial' },
  { label: 'Reconstitution calculator',                              prtcl: 'yes',     doseiq: 'yes',     shotsy: 'no',      peptiq: 'yes'     },
  { label: 'Advanced plans that raise your dose step by step',       prtcl: 'yes',     doseiq: 'partial', shotsy: 'no',      peptiq: 'partial' },
  { label: 'Covers GLP-1s and full-spectrum peptides',               prtcl: 'yes',     doseiq: 'yes',     shotsy: 'no',      peptiq: 'yes'     },
];
const COMPARE_COLS = [
  { key: 'prtcl'  as const, name: 'PRTCL',   sub: 'Built for your first peptide',     highlight: true  },
  { key: 'doseiq' as const, name: 'DoseIQ',  sub: 'Optimization tracker for biohackers', highlight: false },
  { key: 'shotsy' as const, name: 'Shotsy',  sub: 'GLP-1 only (Ozempic, Mounjaro, etc.)',  highlight: false },
  { key: 'peptiq' as const, name: 'PeptIQ',  sub: 'Wearable + bloodwork data correlation', highlight: false },
];

// ----- Features -----
// Differentiator-led (2026-07-22): the old cards were table stakes every
// tracker app lists (tracker, calculator, library). These are the things
// competitors do NOT have, flagship-first, then two even rows of three.
// The basics still show up in the hero tour, walkthrough, and comparison.
// 2026-07-22 pass 2 (Lawrence): dropped the "Doctor Review" card (a second
// vaporware "Coming soon", Pro-only, off-ICP for a first-timer). Added the
// guided-mixing card (the wedge / plastic-bag moment) and a safety card
// ("Your numbers, your call") so the grid is an even six under the flagship.
// Honesty note: only the Masterclass carries `soon` (proposal - see
// docs/2026-07-20-peptide-masterclass-plan.md, nothing built). Every other
// card describes shipped V1 behavior. Citations are backed by the trust
// strip + library entries; the calculator is peptide-agnostic and never
// recommends a dose (marketing context differentiator #6).
const FEATURES = [
  {
    title: 'The Peptide Masterclass',
    body: 'A short course inside the app. It takes you from "what is a peptide?" to running your own plan with confidence. Real research, every claim cited, plain words.',
    tag: 'Short lessons · plain words',
    soon: true,
    flagship: true,
  },
  {
    title: 'Mix your first vial without fear',
    body: 'How much bac water goes in? PRTCL walks you through mixing your first vial, one photo at a time. No shaking, no guessing, no wasted vial.',
    tag: 'Photos at every step',
  },
  {
    title: 'See your dose on the syringe',
    body: 'Other apps hand you a number. PRTCL shows the fill line on a picture of your syringe, so you draw it right the first time.',
    tag: 'Free · any peptide',
  },
  {
    title: 'No dumb questions here',
    body: 'Reddit tells first-timers they should already know. PRTCL never does. Every screen is written for your first time, with zero judgment.',
    tag: 'Plain words · zero judgment',
  },
  {
    title: 'Company for your first shot',
    body: "When it's time to pick up the needle, PRTCL is right there with you. It talks you through the shot, step by step, so you never feel alone.",
    tag: "You're not alone",
  },
  {
    title: 'Every answer shows its source',
    body: 'Every health statement in PRTCL links to where it came from: an FDA label or a published study. You can open the source and read it yourself. No mystery advice.',
    tag: 'Cited sources',
  },
  {
    title: 'Your numbers, your call',
    body: 'PRTCL does your math and shows its work. It never tells you what dose to take. That part stays between you and your doctor.',
    tag: 'The math, not a prescription',
  },
] as const;

// ----- FAQ -----
// Copy voice per .agents/product-marketing-context.md: 5th-grade, kill the
// shame, answer first, then point to a clinician. Pricing facts come from
// src/data/subscriptionPlans.ts ($4.99/mo, $39.99/yr, 14-day trial). The
// old "completely free" answer predates the paywall and was false; the old
// "Over 50 / new ones every week" claims were unverifiable and are gone.
const FAQ_ITEMS = [
  {
    q: 'What makes PRTCL different from other peptide apps?',
    a: "Other peptide apps expect you to know what you are doing already. PRTCL starts at your first vial. It walks you through mixing, measuring, and your first shot, with pictures and plain words. And it never makes you feel dumb for asking.",
  },
  {
    q: 'Is PRTCL a medical app?',
    a: 'No. PRTCL is a learning and tracking tool. It does your math and keeps your plan organized. It does not give medical advice, diagnosis, or treatment. Your doctor is the right person for those questions.',
  },
  {
    q: 'Is my data private?',
    a: 'Yes. Your data is encrypted and stored safely. We never sell it or share it with anyone. You can export it or delete it any time.',
  },
  {
    q: 'What peptides does PRTCL support?',
    a: 'The ones most people start with, and many more: BPC-157, Semaglutide, Tesamorelin, CJC-1295, and the rest of the popular ones. Each entry shows what it does, how to mix it, and what to expect.',
  },
  {
    q: 'Is PRTCL free?',
    a: 'The download is free, and your first 14 days are free. You get the whole app: guided mixing, the calculator, the library, and tracking. After that it is $4.99 a month or $39.99 a year. The mixing calculator on this site is free for everyone.',
  },
  {
    q: 'Is PRTCL on Android?',
    a: 'Not yet. PRTCL is an iPhone app today. The free mixing calculator works in any browser, on any phone.',
  },
  {
    // Post-launch truth (2026-08-12): PRTCL is live on the App Store and
    // the badge opens the real store listing. This copy also feeds the
    // FAQPage JSON-LD that search engines index, so it has to track the
    // actual state of the badge above it.
    q: 'Where can I download PRTCL?',
    a: 'PRTCL is on the App Store now, and the download is free. Tap the App Store button above to get it on your iPhone. The free Peptides 101 guide and the free mixing calculator stay right here on prtclapp.com, no account needed.',
  },
] as const;

// ─────────────────────────────────────────────
// Apple logo SVG (shared across all App Store badges)
// ─────────────────────────────────────────────
export const AppleLogo = ({ size = 'sm' }: { size?: 'sm' | 'md' | 'lg' }) => {
  const dims = size === 'lg' ? { w: 26, h: 32 } : size === 'md' ? { w: 22, h: 26 } : { w: 16, h: 20 };
  return (
    <svg width={dims.w} height={dims.h} viewBox="0 0 14 16" fill="currentColor" aria-hidden="true">
      <path d="M9.7 8.5c0-1.5 1.2-2.3 1.3-2.3-.7-1-1.8-1.2-2.2-1.2-.9-.1-1.8.5-2.3.5s-1.2-.5-2-.5c-1 0-2 .6-2.5 1.5-1.1 1.9-.3 4.6.8 6.1.5.7 1.1 1.6 2 1.5.8 0 1.1-.5 2.1-.5s1.2.5 2 .5c.9 0 1.4-.7 1.9-1.5.6-.8.9-1.7.9-1.7s-1.7-.7-1.7-2.4zm-1.6-4.4c.4-.5.7-1.2.6-1.9-.6 0-1.3.4-1.7.9-.4.4-.7 1.1-.6 1.8.6 0 1.3-.3 1.7-.8z" />
    </svg>
  );
};

// ─────────────────────────────────────────────
// App Store badge — used in nav, hero, CTA
// ─────────────────────────────────────────────
type AppStoreBadgeProps = {
  size?: 'sm' | 'lg';
  onClick?: () => void;
};
export const AppStoreBadge: React.FC<AppStoreBadgeProps> = ({ size = 'sm', onClick }) => {
  const cls = size === 'lg' ? 'lp-app-store-badge--lg' : '';
  const iconSize: 'sm' | 'md' = size === 'lg' ? 'md' : 'sm';
  return (
    <button type="button" className={`lp-app-store-badge ${cls}`} onClick={onClick}>
      <AppleLogo size={iconSize} />
      <span className="lp-app-store-badge__text">
        <span className="lp-app-store-badge__text-sm">Download on the</span>
        <span className="lp-app-store-badge__text-lg">App Store</span>
      </span>
    </button>
  );
};

// ─────────────────────────────────────────────
// Comparison cell icon
// ─────────────────────────────────────────────
const CompareIcon: React.FC<{ value: CellState }> = ({ value }) => {
  if (value === 'yes') {
    return (
      <span className="lp-compare-icon lp-compare-icon--yes" aria-label="yes">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M3 7l2.5 2.5L11 4" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (value === 'partial') {
    return <span className="lp-compare-icon lp-compare-icon--partial" aria-label="partial" />;
  }
  return <span className="lp-compare-icon lp-compare-icon--no" aria-label="no" />;
};

// ─────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────
// `tour` is the homepage experience (promoted from the old /v2 preview on
// 2026-07-23): the hero becomes HeroTour (pinned scroll deck of app screens),
// the tabbed walkthrough becomes the proof strip, the founder chaos cards get
// hover motion, and the nav goes fixed. It is the real, indexed homepage now;
// /v2 301-redirects here (vercel.json). The non-tour branch is retained only
// as a one-flag rollback to the classic hero.
const LandingPage: React.FC<{ tour?: boolean }> = ({ tour = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const pageId = HOME_PAGE;

  // Hydration guard: keep first paint identical for everyone (matches what
  // the prerendered HTML at dist/index.html captured, which has no auth
  // context). After mount, authed users get the Dashboard nav swap on the
  // next render. Prevents the React hydration-mismatch warning that would
  // otherwise fire on every authed visit post-SSR.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Tells the prerender plugin "ready to capture" during npm run build.
  // No-op at runtime.
  usePrerenderSignal();

  // marketing_page_view for the homepage (audit finding #6: it fired zero
  // events). Skip prerender / headless-Chrome traffic so deploy-time captures
  // don't pollute prod analytics, matching the funnel pages' guard.
  useEffect(() => {
    captureTrafficSource();
    if (isAutomatedBrowser()) return;
    void trackEvent('marketing_page_view', { page: pageId, source: getTrafficSource(pageId) });
  }, [pageId]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerCloseRef = useRef<HTMLButtonElement | null>(null);
  const hamburgerRef = useRef<HTMLButtonElement | null>(null);

  // Drawer focus management: move focus to the close button on open,
  // close on Escape, and hand focus back to the hamburger on close.
  // (The closed drawer itself is inert - see the aside - so its links
  // are no longer tabbable while hidden off-screen.)
  useEffect(() => {
    if (!drawerOpen) return;
    drawerCloseRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      hamburgerRef.current?.focus();
    };
  }, [drawerOpen]);
  const [heroIdx, setHeroIdx] = useState(0);
  const [heroPaused, setHeroPaused] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [openFaq, setOpenFaq] = useState<number>(0);

  // Email capture (waitlist) state
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [emailError, setEmailError] = useState('');

  // Auto-advance hero phone tabs.
  // Slowed from 4s to 9s and added one-shot interaction lock so mobile users
  // (who can't hover-pause) can read each phone screen at their own pace.
  // First user click on a tab freezes the carousel until page reload.
  const [heroLocked, setHeroLocked] = useState(false);
  useEffect(() => {
    if (heroPaused || heroLocked) return;
    const t = setInterval(() => {
      setHeroIdx((i) => (i + 1) % HERO_SCREENS.length);
    }, 9000);
    return () => clearInterval(t);
  }, [heroPaused, heroLocked]);

  // FAQPage schema is now rendered via Helmet in the JSX below (replaces the
  // useEffect-injection pattern). Helmet manages it as part of the React tree,
  // which makes it trivially prerender-able once vite-plugin-prerender ships.

  const onAppStore = useCallback(() => {
    void trackEvent('marketing_appstore_click', { page: HOME_PAGE, live: appStoreLive() });
    // One constant drives this: pre-approval we route to the fallback
    // (beta onboarding); on Day 0, marketingConfig's APP_STORE_URL is set
    // and every badge (here + funnel) opens the App Store instead.
    if (appStoreLive() && APP_STORE_URL) {
      window.location.href = APP_STORE_URL;
    } else {
      navigate(APP_STORE_FALLBACK);
    }
  }, [navigate]);

const onEmailSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || emailStatus === 'submitting' || emailStatus === 'success') return;
    setEmailStatus('submitting');
    setEmailError('');
    const source = getTrafficSource(pageId);
    const result = await submitWaitlistEmail(email.trim(), source);
    if (result.success) {
      setEmailStatus('success');
      // waitlist_signup on the homepage form (audit finding #6). Skip
      // duplicates so re-submits aren't counted as fresh conversions, matching
      // the funnel capture hygiene. placement distinguishes this from the
      // funnel hero/calculator captures.
      if (!result.duplicate) {
        void trackEvent('waitlist_signup', { page: pageId, source, magnet: null, placement: 'bottom' });
      }
    } else {
      setEmailStatus('error');
      setEmailError(result.error || 'Something went wrong. Please try again.');
    }
  }, [email, emailStatus, pageId]);

  const scrollTo = useCallback((id: string) => {
    setDrawerOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Hero copy is a single node tree shared by BOTH hero variants (the real
  // homepage hero and /v2's HeroTour) so the preview can never drift from
  // the homepage's headline, lede, or CTA.
  const heroCopy = (
    <div className="lp-hero-copy">
      <h1>
        <span style={{ display: 'block' }}>Your first peptide doesn’t have</span>
        <span style={{ display: 'block' }}>to be <span className="lp-accent">scary.</span></span>
      </h1>
      <p className="lp-hero-lede">
        Not sure how to mix your first vial? Can’t figure out the dosing math?
        Nervous about the needle? PRTCL walks you through mixing, dosing, and
        injecting - step by step, with pictures, so you get it right the first time.
      </p>
      <div className="lp-hero-actions">
        <AppStoreBadge size="lg" onClick={onAppStore} />
      </div>
      {/* Two spans so mobile can break this into two lines with the link
          (the funnel entry) alone on the second one - see hero-tour.css.
          The full stop lives inside the link's span so it never orphans
          onto a third line. */}
      <p className="lp-hero-secondary">
        <span className="lp-hero-secondary__q">New to peptides?</span>{' '}
        <span className="lp-hero-secondary__a">
          <Link to="/your-first-peptide">Start with the free first-timer guide</Link>.
        </span>
      </p>
    </div>
  );

  return (
    <div className={tour ? 'landing landing--tour' : 'landing'}>
      {/* Per-route meta + JSON-LD via react-helmet-async. Currently client-rendered;
          will be SSR-rendered once @prerenderer/rollup-plugin lands.
          See `.agents/ssr-approach-2026-05-09.md` for the implementation plan. */}
      <Helmet>
        <title>PRTCL: Your First Peptide Guide</title>
        <meta name="description" content="Your first peptide doesn't have to be scary. PRTCL walks you from sealed vial to confident first injection with pictures, no math, and a cited source behind every claim. Free walkthrough at prtclapp.com." />
        <link rel="canonical" href="https://www.prtclapp.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.prtclapp.com/" />
        <meta property="og:title" content="PRTCL: Your First Peptide Guide" />
        <meta property="og:description" content="Your first peptide doesn't have to be scary. PRTCL walks you from sealed vial to confident first injection with pictures, no math, and a cited source behind every claim." />
        <meta property="og:image" content="https://www.prtclapp.com/images/og-landing-v2.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="1200" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="PRTCL: Your First Peptide Guide" />
        <meta name="twitter:description" content="Your first peptide doesn't have to be scary. PRTCL walks you from sealed vial to confident first injection with pictures, no math, and a cited source behind every claim." />
        <meta name="twitter:image" content="https://www.prtclapp.com/images/og-landing-v2.png" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQ_ITEMS.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          })),
        })}</script>
      </Helmet>

      {/* ─────────────── NAV ─────────────── */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <a className="lp-wordmark" href="/">PRTCL<span className="lp-wordmark__dot">.</span></a>

          <div className="lp-nav-links">
            <button className="lp-nav-link" onClick={() => scrollTo('features')}>Features</button>
            <button className="lp-nav-link" onClick={() => scrollTo('faq')}>FAQs</button>
            {/* WEB LOCKDOWN (marketingConfig switch): web has NO way into the
                app, so the Dashboard shortcut only appears on native. On web
                every visitor - including a beta user with a lingering session
                - sees the App Store badge. `mounted` gate keeps first paint
                identical to the prerendered HTML. */}
            {mounted && user && isProductSurfaceAllowed() ? (
              <button className="lp-nav-link" onClick={() => navigate('/dashboard')}>Dashboard</button>
            ) : (
              <AppStoreBadge size="sm" onClick={onAppStore} />
            )}
          </div>

          <button ref={hamburgerRef} className="lp-nav-hamburger" onClick={() => setDrawerOpen(true)} aria-label="Open menu" aria-expanded={drawerOpen}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      <div className={`lp-drawer-overlay ${drawerOpen ? 'is-open' : ''}`} onClick={() => setDrawerOpen(false)} />
      <aside className={`lp-drawer ${drawerOpen ? 'is-open' : ''}`} inert={!drawerOpen}>
        <button ref={drawerCloseRef} className="lp-drawer__close" onClick={() => setDrawerOpen(false)} aria-label="Close menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <button className="lp-drawer__link" onClick={() => scrollTo('features')}>Features</button>
        <button className="lp-drawer__link" onClick={() => scrollTo('faq')}>FAQs</button>
        {/* Sign in link removed pending iOS rollout - see nav comment above.
            Dashboard shortcut is native-only (web lockdown). */}
        <div className="lp-drawer__cta">
          {mounted && user && isProductSurfaceAllowed() ? (
            <button className="lp-btn-primary" style={{ width: '100%' }} onClick={() => navigate('/dashboard')}>
              Dashboard
            </button>
          ) : (
            <AppStoreBadge size="lg" onClick={onAppStore} />
          )}
        </div>
      </aside>

      {/* ─────────────── HERO (on /v2: pinned scroll-tour hero) ─────────────── */}
      {tour ? (
      <HeroTour copy={heroCopy} />
      ) : (
      <section className="lp-hero">
        <div className="lp-hero-inner">
          {heroCopy}

          <div
            className="lp-hero-phone-wrap"
            onMouseEnter={() => setHeroPaused(true)}
            onMouseLeave={() => setHeroPaused(false)}
          >
            <div className="lp-hero-tabs" role="tablist" aria-label="App preview tabs">
              {HERO_SCREENS.map((s, i) => (
                <button
                  key={s.label}
                  role="tab"
                  aria-selected={i === heroIdx}
                  className={`lp-hero-tab ${i === heroIdx ? 'is-active' : ''}`}
                  onClick={() => { setHeroIdx(i); setHeroLocked(true); }}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <div className="lp-hero-phone">
              <IOSDevice width={390} height={848} bare>
                <img
                  key={heroIdx}
                  src={HERO_SCREENS[heroIdx].src}
                  alt={`${HERO_SCREENS[heroIdx].label} screen`}
                  className="lp-phone-screenshot"
                />
              </IOSDevice>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ─────────────── FOUNDER NOTE ─────────────── */}
      <section className="lp-founder">
        <div className="lp-founder-inner">
          {/* Left: scattered "before" visual */}
          <div className="lp-founder-stack" aria-hidden="true">
            <div className="lp-card-scattered lp-reddit-card lp-card-r1">
              <div className="lp-reddit-meta">
                <div className="lp-reddit-icon" />
                <span className="lp-reddit-sub">r/Peptides · 4y ago</span>
              </div>
              <div className="lp-card-title">First time mixing BPC-157, am I doing this right??</div>
              <div className="lp-card-meta">28 comments · 4 marked helpful · 3 deleted · 1 mod removed</div>
              <div className="lp-card-quote">"idk man I just shake it"</div>
            </div>

            <div className="lp-card-scattered lp-reddit-card lp-card-r2">
              <div className="lp-reddit-meta">
                <div className="lp-reddit-icon" />
                <span className="lp-reddit-sub">r/SARMs · 2y ago · [removed]</span>
              </div>
              <div className="lp-card-title">NEVER shake your peptides. You'll denature them. (read this)</div>
              <div className="lp-card-meta">412 comments · top reply: "this is wrong"</div>
            </div>

            <div className="lp-card-scattered lp-yt-card lp-card-y1">
              <div className="lp-yt-thumb">
                <div className="lp-yt-play"><div className="lp-yt-play__triangle" /></div>
                <span className="lp-yt-time">14:32</span>
              </div>
              <div className="lp-yt-body">
                <div className="lp-yt-title">HOW I MIX MY PEPTIDES (gone WRONG??)</div>
                <div className="lp-yt-meta">BroScienceMike · 2.4k views · 2021</div>
              </div>
            </div>

            <div className="lp-card-scattered lp-yt-card lp-card-y2">
              <div className="lp-yt-thumb lp-yt-thumb--alt">
                <div className="lp-yt-play lp-yt-play--sm"><div className="lp-yt-play__triangle" /></div>
                <span className="lp-yt-time">27:08</span>
              </div>
              <div className="lp-yt-body">
                <div className="lp-yt-title">The TRUTH about peptide dosing your DR won't tell you</div>
                <div className="lp-yt-meta">AlphaWellness · 18k views · 2020</div>
              </div>
            </div>

            <div className="lp-card-scattered lp-reddit-card lp-card-r3">
              <div className="lp-reddit-meta">
                <div className="lp-reddit-icon" />
                <span className="lp-reddit-sub">r/Peptides · 9mo ago</span>
              </div>
              <div className="lp-card-title">How many units is 0.25 mg?? Math is not mathing</div>
              <div className="lp-card-meta">17 comments · 6 different answers · OP never replied</div>
            </div>
          </div>

          {/* Right: letter */}
          <div>
            <span className="lp-founder-eyebrow">A note from the founder</span>
            <h2 className="lp-founder-title">
              The app I&nbsp;<span className="lp-accent">wished</span>&nbsp;I had.
            </h2>
            <div className="lp-founder-body">
              <p>
                The first time I tried to mix a peptide, I watched four YouTube videos, read three Reddit threads, and still wasn’t sure I’d done it right.
              </p>
              <p>
                The dosing math made no sense. I was scared I’d waste a $100 vial, or worse, hurt myself.
              </p>
              <p>
                So I built PRTCL: the app I wished someone had handed me before I opened that first vial. Clear. Quiet. Specific. The opposite of a forum thread.
              </p>
              <p>
                More people try peptides every day. Most start right where I did: excited, a little scared, and lost in bad information.
              </p>
              <p>
                These things can do real good for your body, but only if you use them right. No one should have to guess. That is why PRTCL exists: to close that gap, and to be the first app that truly teaches a beginner.
              </p>
            </div>

            <div className="lp-signature">
              <div className="lp-signature__avatar">L</div>
              <div className="lp-signature__name">
                <div className="lp-signature__name-text">Lawrence</div>
                <div className="lp-signature__role">Founder · PRTCL</div>
              </div>
              <div className="lp-signature__vial">
                <div>Vial #001 ·</div>
                <div>opened 2026</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── TRUSTED VOICES ─────────────── */}
      <section className="lp-trust-strip" aria-label="Every claim cited to a published source">
        <div className="lp-trust-strip-inner">
          <span className="lp-eyebrow">Every claim cited to a published source</span>
          <p className="lp-trust-strip-lede">
            Every health statement in PRTCL links to where it came from: FDA prescribing information, peer-reviewed research, or published clinical guidance. Anything we could not source, we do not say.
          </p>
          <ul className="lp-trust-pills" role="list">
            {TRUST_PILLS.map((pill) => (
              <li key={pill} className="lp-trust-pill">{pill}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* ─────────────── WALKTHROUGH (on /v2: compact proof strip) ───────────────
          The tour hero already SHOWS the app, so /v2 swaps the tabbed phone
          walkthrough for a text-only proof strip: the 10-minute promise as
          the headline plus the strongest spec bullets, single-sourced from
          WALKTHROUGH_STEPS. (Headline duplicated as JSX only to accent
          "10 minutes" - keep in sync with WALKTHROUGH_STEPS[0].title.) */}
      {tour ? (
      <section className="pv2-proof">
        <div className="pv2-proof-inner">
          <div className="lp-section-head lp-section-head--center">
            <span className="lp-section-head__eyebrow">See how it works</span>
            <h2 className="lp-section-head__title">
              From sealed vial of powder
              <br />
              to your first shot in <span className="lp-accent">10 minutes.</span>
            </h2>
          </div>
          <div className="pv2-proof-grid">
            {([
              { label: 'Mix', step: 0, picks: [0, 2, 3] },
              { label: 'Dose', step: 1, picks: [1, 0, 3] },
              { label: 'Track', step: 2, picks: [0, 1, 3] },
            ] as const).map((col) => (
              <div key={col.label} className="pv2-proof-col">
                <span className="lp-eyebrow">{col.label}</span>
                <ul className="lp-walkthrough-bullets">
                  {col.picks.map((i) => (
                    <li key={i}><span>{WALKTHROUGH_STEPS[col.step].bullets[i]}</span></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
      ) : (
      <section className="lp-walkthrough">
        <div className="lp-walkthrough-inner">
          <div className="lp-walkthrough-card">
            <div>
              <span className="lp-eyebrow">See how it works</span>
              <h2 className="lp-walkthrough-title">{WALKTHROUGH_STEPS[stepIdx].title}</h2>
              <p className="lp-walkthrough-body">{WALKTHROUGH_STEPS[stepIdx].body}</p>
              <ul className="lp-walkthrough-bullets">
                {WALKTHROUGH_STEPS[stepIdx].bullets.map((b) => (
                  <li key={b}><span>{b}</span></li>
                ))}
              </ul>
            </div>

            <div className="lp-walkthrough-phone-col">
              <div className="lp-walkthrough-phone">
                <IOSDevice width={390} height={848} bare>
                  <img
                    key={stepIdx}
                    src={WALKTHROUGH_STEPS[stepIdx].src}
                    alt={WALKTHROUGH_STEPS[stepIdx].title}
                    className="lp-phone-screenshot"
                  />
                </IOSDevice>
              </div>
              <div className="lp-walkthrough-dots" role="tablist" aria-label="Walkthrough steps">
                {WALKTHROUGH_STEPS.map((_, i) => (
                  <button
                    key={i}
                    role="tab"
                    aria-selected={i === stepIdx}
                    aria-label={`Show step ${i + 1}`}
                    className={`lp-walkthrough-dot ${i === stepIdx ? 'is-active' : ''}`}
                    onClick={() => setStepIdx(i)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ─────────────── COMPARISON ─────────────── */}
      <section className="lp-comparison">
        <div className="lp-comparison-inner">
          <div className="lp-section-head lp-section-head--center">
            <span className="lp-section-head__eyebrow">The honest comparison</span>
            <h2 className="lp-section-head__title">
              What you've been doing
              <br />
              is <span className="lp-accent">not working.</span>
            </h2>
            <p className="lp-section-head__lede">
              There are 20+ peptide tracker apps on the App Store. Most are great at what they do - but they were built for people who already know what they're doing. Here's how the real landscape stacks up against PRTCL.
            </p>
          </div>

          <div className="lp-compare-table" role="table">
            <div className="lp-compare-row lp-compare-row--head" role="row">
              <div className="lp-compare-cell lp-compare-cell--label" />
              {COMPARE_COLS.map((c) => (
                <div
                  key={c.key}
                  role="columnheader"
                  className={`lp-compare-cell lp-compare-cell--head ${c.highlight ? 'lp-compare-cell--highlight' : ''}`}
                >
                  <div className="lp-compare-name">{c.name}{c.highlight && '.'}</div>
                  <div className="lp-compare-sub">{c.sub}</div>
                </div>
              ))}
            </div>
            {COMPARE_ROWS.map((r) => (
              <div key={r.label} className="lp-compare-row" role="row">
                <div className="lp-compare-cell lp-compare-cell--label">{r.label}</div>
                {COMPARE_COLS.map((c) => (
                  <div
                    key={c.key}
                    role="cell"
                    className={`lp-compare-cell lp-compare-cell--data ${c.highlight ? 'lp-compare-cell--highlight' : ''}`}
                  >
                    <CompareIcon value={r[c.key]} />
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="lp-compare-legend" aria-hidden="true">
            <span><span className="lp-compare-legend__dot lp-compare-legend__dot--yes" /> Yes</span>
            <span><span className="lp-compare-legend__dot lp-compare-legend__dot--partial" /> Partial</span>
            <span><span className="lp-compare-legend__dot lp-compare-legend__dot--no" /> No</span>
          </div>
        </div>
      </section>

      {/* ─────────────── FEATURES ─────────────── */}
      <section className="lp-features" id="features">
        <div className="lp-features-inner">
          <div className="lp-section-head">
            <span className="lp-section-head__eyebrow">Features</span>
            <h2 className="lp-section-head__title">Made to teach you, not just track you.</h2>
            <p className="lp-section-head__lede">
              Most peptide apps are trackers. PRTCL is the one that teaches you as you go. No social feed, no supplement store, and we never sell peptides. Just tools and straight answers for your whole journey.
            </p>
          </div>

          <div className="lp-features-grid">
            {FEATURES.map((f) => (
              <article key={f.title} className={`lp-feature-card${'flagship' in f && f.flagship ? ' lp-feature-card--flagship' : ''}`}>
                {'soon' in f && f.soon && <span className="lp-feature-card__soon">Coming soon</span>}
                <h3 className="lp-feature-card__title">{f.title}</h3>
                <p className="lp-feature-card__body">{f.body}</p>
                <div className="lp-feature-card__spacer" />
                <div className="lp-feature-card__tag">{f.tag}</div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── FAQ ─────────────── */}
      <section className="lp-faq" id="faq">
        <div className="lp-faq-inner">
          <div className="lp-section-head">
            <span className="lp-section-head__eyebrow">FAQ</span>
            <h2 className="lp-section-head__title">Questions you should ask.</h2>
          </div>
          <div className="lp-faq-list">
            {FAQ_ITEMS.map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={item.q} className={`lp-faq-item ${isOpen ? 'is-open' : ''}`}>
                  <button
                    type="button"
                    className="lp-faq-button"
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                  >
                    <span className="lp-faq-q">{item.q}</span>
                    <span className="lp-faq-toggle">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </span>
                  </button>
                  {isOpen && <div className="lp-faq-answer">{item.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────────────── EMAIL CAPTURE ─────────────── */}
      <section className="lp-email-capture" aria-label="Get short notes from PRTCL">
        <div className="lp-email-capture-inner">
          <div className="lp-section-head lp-section-head--center">
            <span className="lp-section-head__eyebrow">Stay in the loop</span>
            <h2 className="lp-section-head__title">
              Short notes about your first peptide.
            </h2>
            <p className="lp-section-head__lede">
              Drop your email. A few short letters about how to get past the part where you're staring at your first vial - plus the occasional new peptide we add. No marketing tone.
            </p>
          </div>
          <form className="lp-email-capture-form" onSubmit={onEmailSubmit} noValidate>
            <input
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={emailStatus === 'submitting' || emailStatus === 'success'}
              className="lp-email-capture-input"
              aria-label="Email address"
            />
            <button
              type="submit"
              className="lp-btn-primary lp-email-capture-button"
              disabled={!email || emailStatus === 'submitting' || emailStatus === 'success'}
            >
              {emailStatus === 'submitting'
                ? 'Sending...'
                : emailStatus === 'success'
                  ? "You're in."
                  : 'Sign me up'}
            </button>
          </form>
          {emailStatus === 'error' && (
            <p className="lp-email-capture-error" role="alert">{emailError}</p>
          )}
          {emailStatus === 'success' && (
            <p className="lp-email-capture-success" role="status">
              You're on the list. Short notes and new guides, nothing else. PRTCL is on the App Store now, so grab it with the button above whenever you are ready. No spam, ever.
            </p>
          )}
        </div>
      </section>

      {/* ─────────────── FOOTER (CTA + disclaimer) ─────────────── */}
      <div className="lp-footer-top">
        <div className="lp-footer-top-inner">
          <div>
            <h2 className="lp-footer-cta-title">Your first peptide is waiting.</h2>
            <p className="lp-footer-cta-sub">Open PRTCL, follow the steps, and do your first dose with confidence.</p>
            <button className="lp-btn-primary" onClick={onAppStore}>Start your first dose</button>
          </div>
          <p className="lp-disclaimer">
            PRTCL does not provide medical advice, diagnosis, or treatment. Information in
            the app is for educational purposes only. Always consult your healthcare provider
            before starting any peptide protocol.
          </p>
        </div>
      </div>

      {/* Explore strip: keeps the free funnel surfaces one tap from the
          homepage instead of orphaned (audit finding: no landing -> funnel link). */}
      <nav className="lp-footer-explore" aria-label="Explore PRTCL">
        <Link to="/learn">Peptides 101</Link>
        <Link to="/your-first-peptide">Your first peptide</Link>
        <Link to="/reconstitution">Reconstitution</Link>
        <Link to="/tools/reconstitution-calculator">Free calculator</Link>
      </nav>

      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer__wordmark">PRTCL<span className="lp-wordmark__dot">.</span></div>
          <div className="lp-footer__links">
            <Link to="/privacy">Privacy</Link>
            <Link to="/consumer-health-data">Consumer Health Data</Link>
            <Link to="/terms">Terms</Link>
          </div>
          <span className="lp-footer__copy">© {new Date().getFullYear()} PRTCL</span>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
